import axios from "axios";

const BASE = process.env.VUE_APP_BASE_API;

function authHeader() {
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        Authorization: localStorage.getItem('userToken'),
    };
}

function normalizarRespuesta(data) {
    const rows  = data?.message?.rows  ?? data?.rows  ?? [];
    const count = data?.message?.count ?? data?.count ?? rows.length;
    return { rows, count };
}

/**
 * Construye el body para get-query-by-entidades.
 * Los filtros ya vienen completamente formados desde la vista.
 */
function buildQueryBody({ page = 1, size = 15, filtros = [] } = {}) {
    const andBase = filtros.length ? filtros : [{ id: { notEquals: '' } }];
    return {
        entidad: {
            nombre:    'entidad_retiro_independiente',
            idVersion: 1,
            order:     { column: 'id', type: 'desc' },
            size,
            page,
            where:     [{ AND: andBase }],
            relaciones: [],
        },
    };
}

export default {

    // ── Crear ─────────────────────────────────────────────────────────────────
    async crearRetiroIndependiente(data) {
        // 1. Guardar el registro
        const res = await axios({
            url:     `${BASE}/negocio/guardar-entidad`,
            method:  'POST',
            headers: authHeader(),
            data: JSON.stringify({
                nombre_entidad: 'entidad_retiro_independiente',
                id_version: '1',
                data: {
                    ...data,
                    tipo_retiro: 'independiente',
                    estado:       1,
                    createat:     new Date().toISOString(),
                },
            }),
        });
        if (res.status !== 200) throw new Error(res.status);

        return res.data;
    },

    // ── Consultar con paginación y filtros server-side ────────────────────────
    async consultarRetirosIndependientes({ page = 1, size = 15, filtros = [] } = {}) {
        const config = {
            url:     `${BASE}/negocio/get-query-by-entidades`,
            method:  'POST',
            headers: authHeader(),
            data:    JSON.stringify(buildQueryBody({ page, size, filtros })),
        };
        const response = await axios(config);
        if (response.status === 200) return normalizarRespuesta(response.data);
        throw new Error(response.status);
    },

    // Llama con size=1 para obtener solo el count de una condición (liviano)
    async contarRetirosIndependientes(filtros = []) {
        const config = {
            url:     `${BASE}/negocio/get-query-by-entidades`,
            method:  'POST',
            headers: authHeader(),
            data:    JSON.stringify(buildQueryBody({ page: 1, size: 1, filtros })),
        };
        const response = await axios(config);
        if (response.status === 200) return normalizarRespuesta(response.data).count;
        return 0;
    },

    // Una sola llamada que retorna { "1": N, "3": N, "14": N, "13": N, "15": N }
    // filtrosBase: filtros adicionales (ej: id_proveedor, idUsuario) sin incluir estado
    async contarRetirosIndependientesPorEstado(filtrosBase = []) {
        const response = await axios({
            url:     `${BASE}/negocio/contar-retiros-por-estado`,
            method:  'POST',
            headers: authHeader(),
            data: JSON.stringify({
                nombre_entidad: 'entidad_retiro_independiente',
                filtros_base:   filtrosBase,
            }),
        });
        if (response.status === 200) return response.data;
        return {};
    },

    // ── Actualizar ────────────────────────────────────────────────────────────
    async actualizarEstadoRetiro(params) {
        const sanitize = (val) =>
            typeof val === 'string'
                ? val.replace(/\//g, '-').replace(/'/g, '’').replace(/\\/g, '-')
                : val;

        const data = {};
        if (params.estado              !== undefined) data.estado              = params.estado;
        if (params.id_proveedor        !== undefined) data.id_proveedor        = params.id_proveedor;
        if (params.fecha_asignacion    !== undefined) data.fecha_asignacion    = params.fecha_asignacion;
        if (params.fecha_proceso_retiro  !== undefined) data.fecha_proceso_retiro  = params.fecha_proceso_retiro;
        if (params.fecha_retiro_efectivo !== undefined) data.fecha_retiro_efectivo = params.fecha_retiro_efectivo;
        if (params.fecha_retiro_fallido  !== undefined) data.fecha_retiro_fallido  = params.fecha_retiro_fallido;
        if (params.observaciones        !== undefined) data.observaciones        = sanitize(params.observaciones);
        if (params.observaciones_retiro !== undefined) data.observaciones_retiro = sanitize(params.observaciones_retiro);
        if (params.motivo_no_retiro     !== undefined) data.motivo_no_retiro     = params.motivo_no_retiro;

        const config = {
            url:     `${BASE}/negocio/actualizar-campos-retiro`,
            method:  'PUT',
            headers: authHeader(),
            data: JSON.stringify({
                nombre_entidad: 'entidad_retiro_independiente',
                id_metadata:    params.id_metadata,
                data,
            }),
        };
        const response = await axios(config);
        if (response.status === 200) return response.data;
        throw new Error(response.status);
    },

    // ── Histórico / Log ───────────────────────────────────────────────────────

    async consultarHistorial(idRetiro, { soloEstados = false, page = 1, size = 20 } = {}) {
        const andConditions = [{ id: { equals: String(idRetiro) } }];
        if (soloEstados) {
            andConditions.push({ columnasMondificadas: { iLike: '%estado%' } });
        }
        const config = {
            url:     `${BASE}/negocio/get-query-by-entidades`,
            method:  'POST',
            headers: authHeader(),
            data: JSON.stringify({
                entidad: {
                    nombre:    'entidad_retiro_independiente_log',
                    idVersion: 1,
                    order:     { column: 'createat', type: 'desc' },
                    size,
                    page,
                    where:     [{ AND: andConditions }],
                    relaciones: [],
                },
            }),
        };
        const response = await axios(config);
        if (response.status === 200) return normalizarRespuesta(response.data);
        return { rows: [], count: 0 };
    },

    // ── Adjuntos ──────────────────────────────────────────────────────────────

    async subirAdjuntos(idRetiro, archivos) {
        const archivosValidos = archivos.filter(f => f instanceof File);
        if (archivosValidos.length === 0) return null;

        const estructura = {
            nombre_entidad: 'retiro_independiente',
            id_entidad:     idRetiro,
        };
        const formData = new FormData();
        formData.append('nombre_entidad', 'archivos');
        formData.append('estructura', JSON.stringify(estructura));
        archivosValidos.forEach(f => formData.append('entidad', f));

        const response = await axios({
            url:    `${BASE}/negocio/guardar-entidad-archivos`,
            method: 'POST',
            headers: { Authorization: localStorage.getItem('userToken') },
            data:   formData,
        });
        if (response.status === 200) return response.data;
        throw new Error(response.status);
    },

    async consultarAdjuntos(idRetiro) {
        const response = await axios({
            url:    `${BASE}/negocio/consulta-entidad-archivos-id`,
            method: 'POST',
            headers: authHeader(),
            data: JSON.stringify({
                nombre_entidad:         'archivos',
                nombre_entidad_archivo: 'retiro_independiente',
                id_entidad:             idRetiro,
            }),
        });
        if (response.status === 200) return response.data || [];
        return [];
    },

    // ── Proveedores ───────────────────────────────────────────────────────────
    async consultarProveedores() {
        const config = {
            url:     `${BASE}/negocio/get-query-by-entidades`,
            method:  'POST',
            headers: authHeader(),
            data: JSON.stringify({
                entidad: {
                    nombre:    'entidad_proveedor',
                    idVersion: 5,
                    order:     { column: 'id', type: 'asc' },
                    size:      1000,
                    page:      1,
                    where:     [{ AND: [{ id: { notEquals: '' } }] }],
                    relaciones: [],
                },
            }),
        };
        const response = await axios(config);
        if (response.status === 200) return normalizarRespuesta(response.data);
        throw new Error(response.status);
    },
};
